import { ReactNode, useState } from "react";
import { card, colors, type, fontFamily } from "../styles";
import { Freshness, FreshnessChip } from "./Pills";

export interface SeriesDef {
  key: string;
  label: string;
  color: string;
}

export interface ChartTableSpec {
  /** Header for the first (category) column — "Day", "Plan", etc. */
  categoryLabel: string;
  rows: { category: string; values: Record<string, number | string> }[];
  /**
   * Columns for the table, when they are not just the drawn series.
   *
   * Defaults to `series`. The volume charts need this: they draw one series
   * (messages) but the table is the right place to also carry spend, which
   * cannot share the chart's axis — the two differ by three orders of
   * magnitude, so a second bar would sit flat on the baseline. The table view
   * is this dashboard's documented relief for exactly that kind of value, so
   * letting it carry a column the chart does not draw is the point of it.
   */
  columns?: SeriesDef[];
}

/**
 * Card frame for every real chart: title, source subtitle, legend, and a Table
 * toggle rendering the same series as numbers.
 *
 * The table isn't a nicety. Two of the categorical slots this dashboard uses
 * sit below 3:1 contrast on white, and the `dataviz` contrast WARN is not
 * dismissable — it obligates relief. A readable table view of the same series
 * is that relief, so no value on this page is ever recoverable only by
 * distinguishing two fills.
 */
const ChartCard = ({
  title,
  subtitle,
  series,
  table,
  dimmed,
  freshness,
  action,
  children,
}: {
  title: string;
  subtitle?: ReactNode;
  series?: SeriesDef[];
  table?: ChartTableSpec;
  /** True while refetching — keeps the previous render, at reduced opacity. */
  dimmed?: boolean;
  freshness: Freshness;
  /**
   * A control belonging to this chart specifically — the volume charts'
   * day/week/month toggle, say. Sits before the freshness chip so the chip
   * stays in the same place on every card, which is what lets someone scan a
   * column of cards for "which of these is cached".
   */
  action?: ReactNode;
  children: ReactNode;
}) => {
  const [showTable, setShowTable] = useState(false);
  // A single series needs no legend box: the title already names it.
  const showLegend = (series?.length ?? 0) >= 2;
  const tableColumns = table?.columns ?? series;

  return (
    <div style={{ ...card, padding: 16, display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
          <div style={{ ...type.cardTitle, color: colors.ink }}>{title}</div>
          {subtitle && (
            <div style={{ ...type.bodySmallRegular, color: colors.inkFaint }}>{subtitle}</div>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          {action}
          <FreshnessChip freshness={freshness} />
          {table && (
            <button
              onClick={() => setShowTable(current => !current)}
              aria-pressed={showTable}
              style={{
                ...type.bodySmallMedium,
                color: colors.inkMuted,
                background: showTable ? colors.surfaceMuted : "transparent",
                border: `1px solid ${colors.border}`,
                borderRadius: 6,
                padding: "4px 10px",
                cursor: "pointer",
              }}
            >
              {showTable ? "Chart" : "Table"}
            </button>
          )}
        </div>
      </div>

      {showLegend && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 14 }}>
          {series!.map(item => (
            <span
              key={item.key}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, ...type.bodySmallRegular, color: colors.inkMuted }}
            >
              <span
                style={{
                  width: 9,
                  height: 9,
                  borderRadius: 2,
                  background: item.color,
                  flexShrink: 0,
                }}
              />
              {item.label}
            </span>
          ))}
        </div>
      )}

      <div style={{ opacity: dimmed ? 0.6 : 1, transition: "opacity 0.15s" }}>
        {showTable && table ? (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontFamily }}>
              <thead>
                <tr>
                  <th
                    style={{
                      ...type.bodySmallMedium,
                      color: colors.inkFaint,
                      textAlign: "left",
                      padding: "6px 8px",
                      borderBottom: `1px solid ${colors.border}`,
                    }}
                  >
                    {table.categoryLabel}
                  </th>
                  {tableColumns?.map(item => (
                    <th
                      key={item.key}
                      style={{
                        ...type.bodySmallMedium,
                        color: colors.inkFaint,
                        textAlign: "right",
                        padding: "6px 8px",
                        borderBottom: `1px solid ${colors.border}`,
                      }}
                    >
                      {item.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {table.rows.map(row => (
                  <tr key={row.category}>
                    <td
                      style={{
                        ...type.bodySmallRegular,
                        color: colors.ink,
                        padding: "6px 8px",
                        borderBottom: `1px solid ${colors.border}`,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {row.category}
                    </td>
                    {tableColumns?.map(item => (
                      <td
                        key={item.key}
                        style={{
                          ...type.bodySmallRegular,
                          color: colors.ink,
                          padding: "6px 8px",
                          textAlign: "right",
                          borderBottom: `1px solid ${colors.border}`,
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {row.values[item.key] ?? 0}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          children
        )}
      </div>
    </div>
  );
};

export default ChartCard;
